import { MB_RECIPES_SEED_DATA } from '../constants/mbRecipesSeed';
import type { MBCycleRecord } from '../context/MixingAndBakingContext';

export interface ProductOption {
    title: string;
    displayTitle: string;
    srNo: string | number;
}

export function getProductList(): ProductOption[] {
    const active = (MB_RECIPES_SEED_DATA && Array.isArray(MB_RECIPES_SEED_DATA)) 
        ? MB_RECIPES_SEED_DATA.filter((r: any) => r.isActive !== false)
        : [];
    return active.map((r: any) => {
        const skuStr = (r.skuRaw || (Array.isArray(r.sku) ? r.sku.join(", ") : "") || "").trim();
        const title = (r.title || r.variety || "").trim();
        const hasSkuInTitle = skuStr && title.toLowerCase().includes(skuStr.toLowerCase());
        const sameTitleCount = active.filter((other: any) => (other.title || other.variety || "").trim().toLowerCase() === title.toLowerCase()).length;
        const displayTitle = (skuStr && skuStr !== "-" && skuStr.toUpperCase() !== "NA" && !hasSkuInTitle && sameTitleCount > 1)
            ? `${title} (${skuStr})`
            : title;
        return {
            title,
            displayTitle,
            srNo: r.srNo || ''
        };
    });
}

export function findRecipeByTitle(productName: string): any | null {
    if (!productName || !Array.isArray(MB_RECIPES_SEED_DATA)) return null;
    const clean = (s: string) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const target = clean(productName);

    let recipe = MB_RECIPES_SEED_DATA.find((r: any) => {
        const skuStr = (r.skuRaw || (Array.isArray(r.sku) ? r.sku.join(", ") : "") || "").trim();
        const title = (r.title || r.variety || "").trim();
        const displayTitle = (skuStr && skuStr !== "-" && skuStr.toUpperCase() !== "NA" && !title.toLowerCase().includes(skuStr.toLowerCase()))
            ? `${title} (${skuStr})`
            : title;
        return clean(displayTitle) === target || clean(r.title) === target || clean(r.variety) === target;
    });

    if (!recipe) {
        recipe = MB_RECIPES_SEED_DATA.find((r: any) => clean(`${r.title} ${r.skuRaw || ""}`) === target);
    }
    if (!recipe) {
        recipe = MB_RECIPES_SEED_DATA.find((r: any) => {
            const rTitle = clean(r.title || r.variety || "");
            return rTitle && (target.includes(rTitle) || rTitle.includes(target));
        });
    }
    return recipe || null;
}

export function applyRecipeToCycleRecord(record: MBCycleRecord, productName: string): MBCycleRecord {
    const recipe = findRecipeByTitle(productName);
    if (!recipe) return record;

    const s = recipe.standards || recipe;
    const isNA = (v: any) => {
        if (v === undefined || v === null) return true;
        const str = String(v).trim().toUpperCase();
        return str === "" || str === "NA" || str === "N/A" || str === "-";
    };

    const rpoStd = s.rpoStandard !== undefined ? s.rpoStandard : (s.rpo !== undefined ? s.rpo : "45");
    const solidFatStd = s.solidFatStandard !== undefined ? s.solidFatStandard : (s.solidFat !== undefined ? s.solidFat : "NA");
    const butterStd = s.butterStandard !== undefined ? s.butterStandard : (s.butter !== undefined ? s.butter : "NA");
    const blackJackStd = s.blackJackStandard !== undefined ? s.blackJackStandard : (s.blackJack !== undefined ? s.blackJack : "NA");
    const spongeTempStd = s.spongeTempStandard !== undefined ? s.spongeTempStandard : (s.spongeTemp !== undefined ? s.spongeTemp : "NA");
    const slurryStd = s.slurryStandard !== undefined ? s.slurryStandard : (s.slurry !== undefined ? s.slurry : "NA");
    const groundSugarTempStd = s.groundSugarTempStandard !== undefined ? s.groundSugarTempStandard : (s.groundSugarTemp !== undefined ? s.groundSugarTemp : "NA");
    const groundSugarParticleStd = s.groundSugarParticleSizeStandard !== undefined ? s.groundSugarParticleSizeStandard : (s.groundSugarParticleSize !== undefined ? s.groundSugarParticleSize : "NA");

    return {
        ...record,
        productName: productName,
        rpo: {
            standard: String(rpoStd),
            observed: s.rpoObserved !== undefined ? String(s.rpoObserved) : (isNA(rpoStd) ? "NA" : record.rpo?.observed || ""),
            remarks: record.rpo?.remarks || "",
            actionTaken: record.rpo?.actionTaken || ""
        },
        solidFat: {
            standard: String(solidFatStd),
            observed: s.solidFatObserved !== undefined ? String(s.solidFatObserved) : (isNA(solidFatStd) ? "NA" : record.solidFat?.observed || ""),
            remarks: record.solidFat?.remarks || "",
            actionTaken: record.solidFat?.actionTaken || ""
        },
        butter: {
            standard: String(butterStd),
            observed: s.butterObserved !== undefined ? String(s.butterObserved) : (isNA(butterStd) ? "NA" : record.butter?.observed || ""),
            remarks: record.butter?.remarks || "",
            actionTaken: record.butter?.actionTaken || ""
        },
        blackjackInitial: {
            standard: String(blackJackStd),
            observed: s.blackJackObserved !== undefined ? String(s.blackJackObserved) : (isNA(blackJackStd) ? "NA" : record.blackjackInitial?.observed || ""),
            remarks: record.blackjackInitial?.remarks || "",
            actionTaken: record.blackjackInitial?.actionTaken || ""
        },
        sponge: {
            standard: String(spongeTempStd),
            observed: s.spongeTempObserved !== undefined ? String(s.spongeTempObserved) : (isNA(spongeTempStd) ? "NA" : record.sponge?.observed || ""),
            remarks: record.sponge?.remarks || "",
            actionTaken: record.sponge?.actionTaken || ""
        },
        slurry: {
            standard: String(slurryStd),
            observed: s.slurryObserved !== undefined ? String(s.slurryObserved) : (isNA(slurryStd) ? "NA" : record.slurry?.observed || ""),
            remarks: record.slurry?.remarks || "",
            actionTaken: record.slurry?.actionTaken || ""
        },
        groundSugarTemp: {
            standard: String(groundSugarTempStd),
            observed: s.groundSugarTempObserved !== undefined ? String(s.groundSugarTempObserved) : (isNA(groundSugarTempStd) ? "NA" : record.groundSugarTemp?.observed || ""),
            remarks: record.groundSugarTemp?.remarks || "",
            actionTaken: record.groundSugarTemp?.actionTaken || ""
        },
        groundSugarParticleSize: {
            standard: String(groundSugarParticleStd),
            observed: s.groundSugarParticleSizeObserved !== undefined ? String(s.groundSugarParticleSizeObserved) : (isNA(groundSugarParticleStd) ? "NA" : record.groundSugarParticleSize?.observed || ""),
            remarks: record.groundSugarParticleSize?.remarks || "",
            actionTaken: record.groundSugarParticleSize?.actionTaken || ""
        },
        rawMaterials: {
            ...record.rawMaterials,
            chocoChipsSupplier: s.chocoChipsSupplier !== undefined ? String(s.chocoChipsSupplier) : "NA",
            chocoChipsTemp: s.chocoChipsTemp !== undefined ? String(s.chocoChipsTemp) : "NA",
            chocoChipsCountPerKg: s.chocoChipsCountPerKg !== undefined ? String(s.chocoChipsCountPerKg) : "NA",
            chocoChipsCompoundOrPure: s.chocoChipsCompoundOrPure !== undefined ? s.chocoChipsCompoundOrPure : "NA",
            cashewSupplier: s.cashewSupplier !== undefined ? String(s.cashewSupplier) : "NA",
            cashewTemp: s.cashewTemp !== undefined ? String(s.cashewTemp) : "NA",
            cashewCountPerKg: s.cashewCountPerKg !== undefined ? String(s.cashewCountPerKg) : "NA",
            cashewCompoundOrPure: s.cashewCompoundOrPure !== undefined ? s.cashewCompoundOrPure : "NA",
            flourSupplier: s.flourSupplier !== undefined ? String(s.flourSupplier) : "Amul / In-house",
            invertSyrupTemp: s.invertSyrupTemp !== undefined ? String(s.invertSyrupTemp) : "NA",
            invertSyrupPh: s.invertSyrupPh !== undefined ? String(s.invertSyrupPh) : "NA",
            invertSyrupBrix: s.invertSyrupBrix !== undefined ? String(s.invertSyrupBrix) : "NA",
            blackjack2Temp: s.blackJack2Temp !== undefined ? String(s.blackJack2Temp) : "NA",
            blackjack2Ph: s.blackJack2Ph !== undefined ? String(s.blackJack2Ph) : "NA",
            blackjack2Brix: s.blackJack2Brix !== undefined ? String(s.blackJack2Brix) : "NA"
        },
        mixingSponge: {
            ...record.mixingSponge,
            spongeProductName: s.spongeProductName !== undefined ? String(s.spongeProductName) : (s.variety || "NA"),
            spongeWaterQuantity: s.spongeWaterQuantity !== undefined ? String(s.spongeWaterQuantity) : "NA",
            spongeYeastQuantity: s.spongeYeastQuantity !== undefined ? String(s.spongeYeastQuantity) : "NA",
            spongeWaterTemp: s.spongeWaterTemp !== undefined ? String(s.spongeWaterTemp) : "NA",
            spongeMixingTime: s.spongeMixingTime !== undefined ? String(s.spongeMixingTime) : "NA",
            fermentationStartTemp: s.fermentationStartTemp !== undefined ? String(s.fermentationStartTemp) : "NA",
            fermentationRoomTemp: s.fermentationRoomTemp !== undefined ? String(s.fermentationRoomTemp) : "NA",
            finalTempAfterFermentation: s.finalTempAfterFermentation !== undefined ? String(s.finalTempAfterFermentation) : "NA",
            finalPhAfterFermentation: s.finalPhAfterFermentation !== undefined ? String(s.finalPhAfterFermentation) : "NA"
        },
        mixingDough: {
            ...record.mixingDough,
            creamingTimeStd: s.creamingTimeStandard !== undefined ? String(s.creamingTimeStandard) : "10 min",
            creamingTimeObs: s.creamingTimeObserved !== undefined ? String(s.creamingTimeObserved) : record.mixingDough?.creamingTimeObs || "",
            mixingTimeStd: s.mixingTimeStandard !== undefined ? String(s.mixingTimeStandard) : "5 Min",
            mixingTimeObs: s.mixingTimeObserved !== undefined ? String(s.mixingTimeObserved) : record.mixingDough?.mixingTimeObs || "",
            doughTempStd: s.doughTempStandard !== undefined ? String(s.doughTempStandard) : "32-35",
            doughTempObs: s.doughTempObserved !== undefined ? String(s.doughTempObserved) : record.mixingDough?.doughTempObs || "",
            doughStandingTimeStd: s.doughStandingTimeStandard !== undefined ? String(s.doughStandingTimeStandard) : "10 Min",
            doughStandingTimeObs: s.doughStandingTimeObserved !== undefined ? String(s.doughStandingTimeObserved) : record.mixingDough?.doughStandingTimeObs || "",
            doughConsistencyStd: s.doughConsistency !== undefined ? String(s.doughConsistency) : "Smooth / Homogeneous"
        },
        forming: {
            ...record.forming,
            moulderRpmStrokes: s.moulderRpmStrokes !== undefined ? String(s.moulderRpmStrokes) : "NA",
            formingSampleCount: String(s.formingSampleCount !== undefined ? s.formingSampleCount : (s.numberOfBiscuits !== undefined ? s.numberOfBiscuits : "10")),
            standardWetWeight: s.standardWetWeight !== undefined ? String(s.standardWetWeight) : "NA"
        },
        baking: {
            ...record.baking,
            bakingTime: s.bakingTime !== undefined ? String(s.bakingTime) : "4'45\"",
            taste: s.bakingProfileTaste !== undefined ? String(s.bakingProfileTaste) : "Standard / Normal",
            asPerTemplate: s.bakingProfileAsPerTemplate !== false ? "Yes" : "No"
        },
        standards: {
            ...record.standards,
            biscuitLength: String(s.biscuitLength !== undefined ? s.biscuitLength : (s.length !== undefined ? s.length : "NA")),
            biscuitWidth: String(s.biscuitWidth !== undefined ? s.biscuitWidth : (s.width !== undefined ? s.width : "NA")),
            biscuitDiameter: String(s.biscuitDiameter !== undefined ? s.biscuitDiameter : (s.diameter !== undefined ? s.diameter : "NA")),
            biscuitStdWeight: String(s.biscuitStdWeight !== undefined ? s.biscuitStdWeight : (s.dryBiscuitWeight !== undefined ? s.dryBiscuitWeight : "NA")),
            standardsSampleCount: String(s.standardsSampleCount !== undefined ? s.standardsSampleCount : (s.numberOfBiscuits !== undefined ? s.numberOfBiscuits : "10")),
            topColourStd: s.topColourStandard !== undefined ? String(s.topColourStandard) : "As per std",
            topColourObs: s.topColourObserved !== undefined ? String(s.topColourObserved) : "As per std",
            bottomColourStd: s.bottomColourStandard !== undefined ? String(s.bottomColourStandard) : "As per std",
            bottomColourObs: s.bottomColourObserved !== undefined ? String(s.bottomColourObserved) : "As per std",
            moistureStd: s.moistureStandard !== undefined ? String(s.moistureStandard) : (s.moisture !== undefined ? String(s.moisture) : "2.25%"),
            weightAfterOilSpray: String(s.weightAfterOilSpray !== undefined ? s.weightAfterOilSpray : (s.weightAfterOil !== undefined ? s.weightAfterOil : "NA"))
        }
    };
}
